import { useCallback, useEffect, useRef } from 'react';
import type { BoardIdentifyResponse, OsType } from '@/stores/useBoardEditorStore';
import { DetectedBoard } from '@/stores/useBoardEditorStore';

interface UseBoardBroadcastOptions {
    board: 'orange' | 'blue';
    port: string | null;
    baud: number;
    hasSelectedBoard: boolean;
    isWebSocketConnected: boolean;
    isBridgeConnected: boolean;
    isBoardConnected: boolean;
    isDark: boolean;
    // シリアルモニター窓側で、USB接続時はboard_name、Bluetooth接続時はbluetooth_device_nameを
    // 出し分けて表示するために必要(以前は一切ブロードキャストされておらず、モニター側は
    // chip_id由来の識別子を一切知らなかった)。
    boardIdentity: BoardIdentifyResponse | null;
    // モニター側のモーダル(MonitorBoardSelector.tsx)がhub所属バッジを「まだ解決中(非表示)」
    // 「解決済みだが未所属/クラウド照会失敗(unknown表示)」で区別するために必要。
    // boardIdentityがnullなだけでは、この2つの状態を見分けられない。
    chipIdQueryStatus: 'idle' | 'pending' | 'succeeded' | 'timed_out';
    monitorXMode: any;
    monitorTimeWindow: any;
    monitorYMode: any;
    monitorYMin: any;
    monitorYMax: any;
    osType: OsType;
    isUploading: boolean;
    detectedBoards: DetectedBoard[];
    setIsMonitorOpen: (isOpen: boolean) => void;
    writeToSerial?: (message: string) => void;
    setMonitorXMode: (val: any) => void;
    setMonitorTimeWindow: (val: any) => void;
    setMonitorYMode: (val: any) => void;
    setMonitorYMin: (val: any) => void;
    setMonitorYMax: (val: any) => void;
}

interface UseBoardBroadcastReturn {
    // ブリッジがchip_id読み取り/アップロードのためにモニターを一時停止/再開した際、
    // モニター窓へそのまま転送するための関数(BoardToolbar.tsx参照)。
    sendSerialMonitorStatus: (port: string, status: string) => void;
}

export function useBoardBroadcast(options: UseBoardBroadcastOptions): UseBoardBroadcastReturn {
    const {
        board,
        port,
        baud,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isDark,
        boardIdentity,
        chipIdQueryStatus,
        monitorXMode,
        monitorTimeWindow,
        monitorYMode,
        monitorYMin,
        monitorYMax,
        osType,
        isUploading,
        detectedBoards,
        setIsMonitorOpen,
        writeToSerial,
        setMonitorXMode,
        setMonitorTimeWindow,
        setMonitorYMode,
        setMonitorYMin,
        setMonitorYMax,
    } = options;

    const dataRef = useRef({
        board,
        port,
        baud,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isDark,
        detectedBoards,
        boardIdentity,
        chipIdQueryStatus,
    });

    const writeToSerialRef = useRef(writeToSerial);
    const prevIsUploadingRef = useRef<boolean | undefined>(undefined);
    const bcRef = useRef<BroadcastChannel | null>(null);
    const osTypeRef = useRef(osType);
    const settingsRef = useRef({
        monitorXMode,
        monitorTimeWindow,
        monitorYMode,
        monitorYMin,
        monitorYMax,
    });

    const settersRef = useRef({
        setMonitorXMode,
        setMonitorTimeWindow,
        setMonitorYMode,
        setMonitorYMin,
        setMonitorYMax,
    });

    useEffect(() => {
        // apiFetch()は全レスポンスに生のHeadersオブジェクトを__headersとして付与しており、
        // boardIdentityはその戻り値をそのまま流用しているため、この非クローン可能なプロパティが
        // 混ざっている。BroadcastChannel.postMessage()は構造化クローンを使うため、そのまま渡すと
        // 「Headers object could not be cloned」で例外になる(実際に発生した不具合)。
        // 必要なフィールドだけを持つプレーンなオブジェクトに詰め替えてから保持する。
        const cleanBoardIdentity: BoardIdentifyResponse | null = boardIdentity
            ? {
                  is_registered: boardIdentity.is_registered,
                  board_id: boardIdentity.board_id,
                  cd: boardIdentity.cd,
                  board_name: boardIdentity.board_name,
                  hub_id: boardIdentity.hub_id,
                  hub_name: boardIdentity.hub_name,
                  hub_type_cd: boardIdentity.hub_type_cd,
                  hub_type_name: boardIdentity.hub_type_name,
                  bluetooth_device_name: boardIdentity.bluetooth_device_name,
              }
            : null;
        dataRef.current = {
            board,
            port,
            baud,
            hasSelectedBoard,
            isWebSocketConnected,
            isBridgeConnected,
            isBoardConnected,
            isDark,
            detectedBoards,
            boardIdentity: cleanBoardIdentity,
            chipIdQueryStatus,
        };
    }, [
        board,
        port,
        baud,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isDark,
        detectedBoards,
        boardIdentity,
        chipIdQueryStatus,
    ]);

    useEffect(() => {
        settingsRef.current = { monitorXMode, monitorTimeWindow, monitorYMode, monitorYMin, monitorYMax };
        settersRef.current = { setMonitorXMode, setMonitorTimeWindow, setMonitorYMode, setMonitorYMin, setMonitorYMax };
    }, [
        monitorXMode,
        monitorTimeWindow,
        monitorYMode,
        monitorYMin,
        monitorYMax,
        setMonitorXMode,
        setMonitorTimeWindow,
        setMonitorYMode,
        setMonitorYMin,
        setMonitorYMax,
    ]);

    useEffect(() => {
        osTypeRef.current = osType;
    }, [osType]);

    useEffect(() => {
        writeToSerialRef.current = writeToSerial;
    }, [writeToSerial]);

    useEffect(() => {
        bcRef.current = new BroadcastChannel('schoomy-board');
        const sendData = () => {
            bcRef.current?.postMessage({
                type: 'board_info',
                ...dataRef.current,
                osType: osTypeRef.current,
                graphSettings: {
                    xMode: settingsRef.current.monitorXMode,
                    timeWindow: settingsRef.current.monitorTimeWindow,
                    yMode: settingsRef.current.monitorYMode,
                    yMin: settingsRef.current.monitorYMin,
                    yMax: settingsRef.current.monitorYMax,
                },
            });
        };

        sendData();
        // 親タブが(再読み込み等で)新しく立ち上がるたびに、既に開いているモニター窓があるかを
        // 確認する。isMonitorOpen(store)もmonitorWindowRef(BoardToolbar.tsx側のuseRef)も
        // ページ全体のリロードで消えてしまうため、親をリロードした状態でモニターを開くと
        // 「既に開いている」ことを検知できず、二重に窓を開いてしまう不具合が実際にあった。
        // モニター窓側がまだ生きていれば'monitor_status'で応答してくれる(useMonitorBroadcast.ts参照)。
        bcRef.current.postMessage({ type: 'request_monitor_status' });

        bcRef.current.onmessage = (event) => {
            if (event.data.type === 'request_initial_data') {
                sendData();
            }
            if (event.data.type === 'monitor_status' && typeof event.data.isOpen === 'boolean') {
                setIsMonitorOpen(event.data.isOpen);
            }
            if (event.data.type === 'send_serial' && typeof event.data.message === 'string') {
                writeToSerialRef.current?.(event.data.message);
            }
            if (event.data.type === 'update_graph_settings' && event.data.settings) {
                const s = event.data.settings;
                const set = settersRef.current;
                if (s.xMode !== undefined) set.setMonitorXMode(s.xMode);
                if (s.timeWindow !== undefined) set.setMonitorTimeWindow(s.timeWindow);
                if (s.yMode !== undefined) set.setMonitorYMode(s.yMode);
                if (s.yMin !== undefined) set.setMonitorYMin(s.yMin);
                if (s.yMax !== undefined) set.setMonitorYMax(s.yMax);
            }
        };

        return () => bcRef.current?.close();
    }, []);

    useEffect(() => {
        bcRef.current?.postMessage({ type: 'board_info', ...dataRef.current });
    }, [
        board,
        port,
        baud,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isDark,
        detectedBoards,
        boardIdentity,
        chipIdQueryStatus,
    ]);

    useEffect(() => {
        if (prevIsUploadingRef.current === undefined) {
            prevIsUploadingRef.current = isUploading;
            return;
        }
        if (prevIsUploadingRef.current !== isUploading) {
            if (isUploading) {
                bcRef.current?.postMessage({ type: 'upload_start' });
            } else {
                bcRef.current?.postMessage({ type: 'upload_complete' });
            }
            prevIsUploadingRef.current = isUploading;
        }
    }, [isUploading]);

    const sendSerialMonitorStatus = useCallback((port: string, status: string) => {
        bcRef.current?.postMessage({ type: 'serial_monitor_status', port, status });
    }, []);

    return { sendSerialMonitorStatus };
}
